import { useCallback, useEffect, useMemo, useState } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { GapRepresentativeValue } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import {
  MONTH_DUR_OPTIONS,
  SUPPLY_GAP_FILTERS_STORAGE_KEY,
  WEEK_DUR_OPTIONS,
  supplyGapFiltersCodec,
} from '~/pages/SupplyGap/models/persistedFilters'
import { type BreakdownMode } from '~/pages/SupplyGap/models/supplyCategories'
import {
  type GapFilterState,
  type RangeType,
} from '~/pages/SupplyGap/models/types'

/**
 * `selectedAreaCodes` (空 = 全業務エリアのポジティブ選択モデル) を正規化する:
 * - 業務エリアから外れた stale code を除く
 * - 全業務エリアを明示選択した状態は空へ畳む (design js/L1224-1228 の
 *   `sel.length === AREAS.length` 正規化と同じ。 空に戻さないと全選択後に
 *   「減らす」操作の起点が無くなる違和感が残る)
 *
 * 旧バージョン (checkbox 実装時代) が保存した「全業務エリアを明示列挙した配列」も
 * 本関数で新形式 (空 = 全エリア) へ畳まれるため、 localStorage の stale データからの
 * 移行もこの関数だけで安全になる。
 */
function normalizeSelectedAreaCodes(
  selected: AreaCode[],
  businessAreaCodes: AreaCode[],
): AreaCode[] {
  if (selected.length === 0) return selected
  const valid = selected.filter((c) => businessAreaCodes.includes(c))
  // 業務エリアが 1 件だけの組織では畳まない。 畳むと「唯一の候補を選ぶ」 →
  // 即座に空 (= 直前と同値) になり chip も出ず、 **本 PR が直した dead click と
  // 同じ「押しても何も起きない」体験**が復活する。 候補が 1 件なら「全選択」と
  // 「1 件選択」が同義で、 畳む動機 (全選択後に減らす起点が無くなる) 自体が無い。
  if (
    businessAreaCodes.length > 1 &&
    valid.length === businessAreaCodes.length
  ) {
    return []
  }
  return valid
}

/**
 * mount 時の初期 state。 直列化・復元・既定値・検証は
 * `models/persistedFilters.ts` の純関数が SoT。
 *
 * 復元値には続けて `normalizeSelectedAreaCodes` を通す (旧 checkbox 時代の
 * 「全業務エリアを明示列挙した配列」を新形式の空へ畳む)。 **この畳み込みは
 * in-memory のみで保存はしない** — 業務エリア設定は取得前に全エリアへ
 * フォールバックするため、 早すぎるタイミングの正規化結果を焼き付けない。
 */
function buildInitialState(businessAreaCodes: AreaCode[]): GapFilterState {
  const restored = supplyGapFiltersCodec.deserialize(
    getLocalStorageItem(SUPPLY_GAP_FILTERS_STORAGE_KEY),
  )
  return {
    ...restored,
    selectedAreaCodes: normalizeSelectedAreaCodes(
      restored.selectedAreaCodes,
      businessAreaCodes,
    ),
  }
}

export type UseFilterStateReturn = {
  filter: GapFilterState
  setRangeType: (v: RangeType) => void
  setDayDate: (v: string) => void
  setWeekStart: (v: string) => void
  setWeekDur: (v: number) => void
  setMonthStart: (v: string) => void
  setMonthDur: (v: number) => void
  setRepresentativeValue: (v: GapRepresentativeValue) => void
  setBreakdownMode: (v: BreakdownMode) => void
  setSelectedAreaCodes: (v: AreaCode[]) => void
  weekDurOptions: readonly number[]
  monthDurOptions: readonly number[]
  /** 週間モード時の表示範囲ラベル (例: "6/1 〜 6/7") */
  weekRangeLabel: string
  /** 月間モード時の表示範囲ラベル (例: "6月〜8月") */
  monthRangeLabel: string
}

export function useFilterState(
  businessAreaCodes: AreaCode[],
): UseFilterStateReturn {
  const [filter, setFilter] = useState<GapFilterState>(() =>
    buildInitialState(businessAreaCodes),
  )

  // 業務エリア設定が変わったとき、 selectedAreaCodes が有効な値であるか確認。
  // 空 (= 全エリア) はそのまま据置き (businessAreaCodes 側が変わっても「全エリア」の
  // 意味は変わらない)。 非空なら stale code の除去 + 全件一致の畳み込みを行う。
  // 注: update() でなく setFilter() を直接呼ぶため localStorage へは保存しない
  // (= 既存の意図的な非対称。 直さない発見として報告済み)。
  //
  // **この非対称は共通 hook `~/hooks/usePersistedFilters` へそのまま移せない。**
  // 同 hook の `updateFilters` は updater が `prev` を返しても必ず新しい参照を作る
  // ため、 ここを載せ替えると「未操作の初期 mount では書かない」性質 (同 hook の
  // godoc: ServerClockGate の待ち上限経路で端末時計由来の初期値が焼き付く) が
  // この画面で崩れる。 移行するなら非対称の畳み方とセットで設計すること。
  useEffect(() => {
    if (businessAreaCodes.length === 0) return
    setFilter((prev) => {
      if (prev.selectedAreaCodes.length === 0) return prev
      const normalized = normalizeSelectedAreaCodes(
        prev.selectedAreaCodes,
        businessAreaCodes,
      )
      const unchanged =
        normalized.length === prev.selectedAreaCodes.length &&
        normalized.every((c, i) => c === prev.selectedAreaCodes[i])
      if (unchanged) return prev
      return { ...prev, selectedAreaCodes: normalized }
    })
  }, [businessAreaCodes])

  // 保存の契機は**利用者の操作 (update() 呼び出し) だけ**。 未操作の mount や
  // 上の正規化 effect では書かない。
  const update = useCallback((partial: Partial<GapFilterState>) => {
    setFilter((prev) => {
      const next = { ...prev, ...partial }
      setLocalStorageItem(
        SUPPLY_GAP_FILTERS_STORAGE_KEY,
        supplyGapFiltersCodec.serialize(next),
      )
      return next
    })
  }, [])

  const setRangeType = useCallback(
    (v: RangeType) => update({ rangeType: v }),
    [update],
  )
  const setDayDate = useCallback(
    (v: string) => update({ dayDate: v }),
    [update],
  )
  const setWeekStart = useCallback(
    (v: string) => update({ weekStart: v }),
    [update],
  )
  const setWeekDur = useCallback(
    (v: number) => update({ weekDur: v }),
    [update],
  )
  const setMonthStart = useCallback(
    (v: string) => update({ monthStart: v }),
    [update],
  )
  const setMonthDur = useCallback(
    (v: number) => update({ monthDur: v }),
    [update],
  )
  const setRepresentativeValue = useCallback(
    (v: GapRepresentativeValue) => update({ representativeValue: v }),
    [update],
  )
  const setBreakdownMode = useCallback(
    (v: BreakdownMode) => update({ breakdownMode: v }),
    [update],
  )
  const setSelectedAreaCodes = useCallback(
    (v: AreaCode[]) => {
      // bug #dead-click (2026-08-12): 旧実装は `v.length === 0` を no-op にしており、
      // 「全エリア checkbox」や「残り 1 件を外す」操作が触っても何も起きない dead
      // click になっていた (spec `supply-demand-gap.md:76` の「最低 1 エリア必須」を
      // 空配列拒否で愚直に実装した結果)。 空配列は「全業務エリアを対象にする」の
      // 意味へ再定義し (= 実際の最低 1 エリア保証は resolveQueryAreaCodes による
      // 全業務エリアへの展開で満たす)、 guard を撤廃して常に反映する。
      update({
        selectedAreaCodes: normalizeSelectedAreaCodes(v, businessAreaCodes),
      })
    },
    [update, businessAreaCodes],
  )

  // 週間: 開始日から weekDur*7 日後の範囲ラベル
  const weekRangeLabel = useMemo(() => {
    try {
      const parts = filter.weekStart.split('-')
      if (parts.length !== 3) return ''
      const start = new Date(+parts[0], +parts[1] - 1, +parts[2])
      const end = new Date(start)
      end.setDate(start.getDate() + filter.weekDur * 7 - 1)
      return `${start.getMonth() + 1}/${start.getDate()} 〜 ${end.getMonth() + 1}/${end.getDate()}`
    } catch {
      return ''
    }
  }, [filter.weekStart, filter.weekDur])

  // 月間: 開始月から monthDur 月後の範囲ラベル
  const monthRangeLabel = useMemo(() => {
    try {
      const parts = filter.monthStart.split('-')
      if (parts.length !== 3) return ''
      const start = new Date(+parts[0], +parts[1] - 1, 1)
      const end = new Date(
        start.getFullYear(),
        start.getMonth() + filter.monthDur - 1,
        1,
      )
      return `${start.getMonth() + 1}月 〜 ${end.getMonth() + 1}月`
    } catch {
      return ''
    }
  }, [filter.monthStart, filter.monthDur])

  return {
    filter,
    setRangeType,
    setDayDate,
    setWeekStart,
    setWeekDur,
    setMonthStart,
    setMonthDur,
    setRepresentativeValue,
    setBreakdownMode,
    setSelectedAreaCodes,
    weekDurOptions: WEEK_DUR_OPTIONS,
    monthDurOptions: MONTH_DUR_OPTIONS,
    weekRangeLabel,
    monthRangeLabel,
  }
}
